import Image from "next/image";
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
import Link from "next/link";

interface RelatedProceduresData {
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        collection: string;
        item: {
          title: string;
          text: string;
          alt: string;
          image_file: {
            image: {
              id: string;
              filename_download: string;
            };
          };
        };
      }[];
    };
  }[];
}

export const fields = [
  "heading.text",
  "heading.title",

  "subheading.text",
  "subheading.title",

  "content_collection.id",
  "content_collection.site_content_collections_id.collection.collection",
  "content_collection.site_content_collections_id.collection.item:site_content.title",
  "content_collection.site_content_collections_id.collection.item:site_content.text",

  "content_collection.site_content_collections_id.collection.item:images.alt",
  "content_collection.site_content_collections_id.collection.item:images.image_file.image.id",
  "content_collection.site_content_collections_id.collection.item:images.image_file.image.filename_download",
] as const;

interface RelatedProceduresProps {
  data?: RelatedProceduresData;
}

export default function RelatedProcedures({ data }: RelatedProceduresProps) {
  return (
    <section className="w-full bg-white px-8 xl:px-16 py-8 xl:py-32">
      <div className="container mx-auto">
        <div className="mb-6 flex flex-col gap-4 md:items-center md:mb-9 md:gap-9">
          <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-left md:text-center text-primary">
            {data?.heading?.title}
          </h2>
          <div className="text-left md:text-center font-greycliff text-[1rem] font-normal leading-normal md:max-w-[55rem] md:text-xl md:leading-7 text-primary [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal md:[&>*]:text-xl md:[&>*]:leading-7">
            <div
              dangerouslySetInnerHTML={sanitizeHTML(
                data?.subheading?.title || data?.subheading?.text || ""
              )}
            />
          </div>
        </div>
        <div className="flex flex-col gap-4 md:flex-row md:gap-[2.2rem]">
          {data?.content_collection?.map((collection, index) => {
            const imageItem =
              collection?.site_content_collections_id?.collection.find(
                (item) => item.collection === "images"
              )?.item;
            const titleItem =
              collection?.site_content_collections_id?.collection.find(
                (item) => item.collection === "site_content" && item.item.title
              )?.item;
            const textItem =
              collection?.site_content_collections_id?.collection.find(
                (item) => item.collection === "site_content" && item.item.text
              )?.item;

            const imageId = imageItem?.image_file?.image?.id;
            const imageFilename =
              imageItem?.image_file?.image?.filename_download;
            const imageAlt = imageItem?.alt;
            const title = titleItem?.title;
            const text = textItem?.text;

            // Extract href from title if it contains a link
            const titleHref = title?.match(/<a\s+(?:[^>]*?\s+)?href="([^"]*)"[^>]*>/)?.[1];

            // Determine if we need to wrap content in a link
            const WrapperComponent = titleHref ? 'a' : 'div';
            const wrapperProps = titleHref ? { href: titleHref, className: "group relative flex flex-col md:w-1/3" } : { className: "group relative flex flex-col md:w-1/3" };

            return (
              <WrapperComponent key={index} {...wrapperProps}>
                <div className="relative h-72 overflow-hidden rounded-[1.25rem]">
                  <Image
                    src={
                      imageId && imageFilename
                        ? getReadableImageUrl(imageId, imageFilename)
                        : ""
                    }
                    alt={imageAlt || ""}
                    fill
                    className="object-cover transition-transform duration-500 md:group-hover:scale-150"
                    sizes="(max-width: 768px) 100vw, 33vw"
                    quality={65}
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary opacity-70 md:hidden" />
                </div>
                <div className="absolute bottom-0 left-0 p-6 md:static md:p-0 md:pt-6">
                  <h3
                    className="text-3xl xl:text-4xl mb-4 font-essonnes font-light text-white md:text-[2.5rem] md:leading-[3.4rem] md:text-primary"
                    dangerouslySetInnerHTML={sanitizeHTML(
                      titleHref
                        ? (title || "").replace(/<a[^>]*>([\s\S]*?)<\/a>/gi, "$1")
                        : (title || "")
                    )}
                  />
                  <p
                    className="font-greycliff text-[1rem] font-normal leading-normal text-white md:text-xl md:leading-7 md:text-primary [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal md:[&>*]:text-xl md:[&>*]:leading-7 [&>a]:underline [&>a:hover]:no-underline [&>p>a]:underline [&>p>a:hover]:no-underline"
                    dangerouslySetInnerHTML={sanitizeHTML(text || "")}
                  />
                </div>
              </WrapperComponent>
            );
          })}
        </div>
      </div>
    </section>
  );
}
